import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { showDateFormat } from '@utils'

const getCompletedIndex = (status) => {
	if (status === 'DELIVERED') return 3
	if (status === 'EN ROUTE') return 2
	if (status === 'LOADED') return 1
	return 0
}

/**
 * Non-API state for the Invoice "Basic Details" tab: sources invoice/order from
 * the drawer state and computes the distance-tracker derivations that feed
 * <DistanceProgress>. All API state/handlers live in the individual cards.
 */
const useBasicDetails = () => {
	const invoiceData = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'invoiceData']))
	const order = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'order']))
	const completedIndex = getCompletedIndex(invoiceData?.delivery_status)
	const vehicleStatus = invoiceData?.vehicle_status_list?.[invoiceData.vehicle_status_list.length - 1]
	const tot = vehicleStatus?.total_distance ?? 0
	const dist = vehicleStatus?.distance_travelled ?? 0

	const { loadedDate, inTransitDate, deliveredDate } = useMemo(
		() => ({
			loadedDate: completedIndex >= 1 && invoiceData?.invoice_date ? showDateFormat(new Date(invoiceData.invoice_date)) : '-',
			inTransitDate: completedIndex >= 2 ? showDateFormat(new Date()) : null,
			deliveredDate: completedIndex >= 3 && invoiceData?.delivery_date ? showDateFormat(new Date(invoiceData?.delivery_date)) : null,
		}),
		[invoiceData, completedIndex]
	)

	const fillPercent = completedIndex === 2 ? Math.min(100, tot > 0 ? (dist / tot) * 100 : 0) : (Math.max(0, completedIndex - 1) / 2) * 100
	const displayTotal = Math.round(tot)
	const displayCovered = Math.round(dist)
	const displayRemaining = Math.round(Math.max(0, tot - dist))
	const showTransit = completedIndex >= 1
	const transitPercent = completedIndex === 2 ? fillPercent : 50
	const transitDone = completedIndex >= 2
	
	return {
		invoiceData,
		order,
		completedIndex,
		fillPercent,
		showTransit,
		transitPercent,
		transitDone,
		displayTotal,
		displayCovered,
		displayRemaining,
		loadedDate,
		inTransitDate,
		deliveredDate,
	}
}

export default useBasicDetails
